import { useEffect, useState } from "react";
import {
    BadgePercent,
    Check,
    ChevronRight,
    Heart,
    PackageCheck,
    ShieldCheck,
    ShoppingCart,
    Star,
    Truck,
    X,
    Zap,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useCart } from "../../../../../../context/CartContext";
import { useWishlist } from "../../../../../../context/WishlistContext";
import {
    FashionProductItem,
    ProductColor,
    formatPrice,
} from "../../fashionTypes";
import "./WomenProductOptionsModal.css";

export type { ProductColor };
export type WomenFashionProduct = FashionProductItem;

type Props = {
    product: WomenFashionProduct | null;
    onClose: () => void;
};

const DEFAULT_SIZES = ["XS", "S", "M", "L", "XL", "XXL"];

const WomenProductOptionsModal = ({ product, onClose }: Props) => {
    const { addToCart } = useCart();
    const { isItemWishlisted, toggleWishlist: toggleWishlistContext } = useWishlist();
    const navigate = useNavigate();

    const [selectedColor, setSelectedColor] = useState(0);
    const [selectedSize, setSelectedSize] = useState("M");
    const [wishlisted, setWishlisted] = useState(false);
    const [addedToBag, setAddedToBag] = useState(false);

    useEffect(() => {
        if (!product) return;

        setSelectedColor(0);
        const availableSizes =
            product.sizes && product.sizes.length > 0 ? product.sizes : DEFAULT_SIZES;
        setSelectedSize(availableSizes.includes("M") ? "M" : availableSizes[0] || "M");
        setWishlisted(isItemWishlisted(String(product.id)));
        setAddedToBag(false);

        const handleEscape = (event: KeyboardEvent) => {
            if (event.key === "Escape") {
                onClose();
            }
        };

        document.body.style.overflow = "hidden";
        window.addEventListener("keydown", handleEscape);

        return () => {
            document.body.style.overflow = "";
            window.removeEventListener("keydown", handleEscape);
        };
    }, [product, onClose, isItemWishlisted]);

    if (!product) return null;

    const discount = Math.round(
        ((product.mrp - product.price) / product.mrp) * 100
    );

    const smartPrice = Math.max(
        Math.round(product.price - product.price * 0.08),
        1
    );

    const smartSaving = product.price - smartPrice;

    const activeImage =
        product.colors[selectedColor]?.image || product.image;

    const availableSizes =
        product.sizes && product.sizes.length > 0 ? product.sizes : DEFAULT_SIZES;

    const handleWishlistToggle = () => {
        setWishlisted((prev) => !prev);
        if (toggleWishlistContext) {
            toggleWishlistContext(String(product.id));
        }
    };

    const handleAddToCart = () => {
        addToCart({
            productId: product.id,
            title: product.title,
            description: product.description,
            image: activeImage,
            price: product.price,
            mrp: product.mrp,
            color: product.colors[selectedColor]?.name,
            size: selectedSize,
        });

        setAddedToBag(true);
    };

    const handleViewDetails = () => {
        onClose();
        const targetId = product._id || product.id || product.ssin;
        navigate(`/DetailPage/${targetId}`, {
            state: {
                product: {
                    ...product,
                    category: "Women Fashion",
                    breadcrumb: [
                        "Home",
                        "Women",
                        "Fashion & Accessories",
                        product.title,
                    ],
                },
            },
        });
    };

    return (
        <div
            className="women-options-overlay"
            onMouseDown={(event) => {
                if (event.target === event.currentTarget) {
                    onClose();
                }
            }}
        >
            <div
                className="women-options-modal"
                role="dialog"
                aria-modal="true"
                aria-label={`${product.title} options`}
            >
                <button
                    type="button"
                    className="women-options-close"
                    onClick={onClose}
                    aria-label="Close"
                >
                    <X size={20} strokeWidth={1.8} />
                </button>

                <div className="women-options-left">
                    <div className="women-options-main-image">
                        <span className="women-options-image-badge">
                            {product.badge}
                        </span>

                        <button
                            type="button"
                            className={`women-options-wishlist ${wishlisted ? "active" : ""
                                }`}
                            onClick={handleWishlistToggle}
                            aria-label="Wishlist"
                        >
                            <Heart
                                size={19}
                                strokeWidth={2}
                                fill={
                                    wishlisted
                                        ? "currentColor"
                                        : "none"
                                }
                            />
                        </button>

                        <img
                            src={activeImage}
                            alt={product.title}
                            onError={(e) => {
                                e.currentTarget.src = "/assets/images/products/women-fas/women-1.png";
                            }}
                        />
                    </div>

                    <div className="women-options-preview-list">
                        {product.colors.map((color, index) => (
                            <button
                                type="button"
                                key={color.name}
                                className={`women-options-preview ${selectedColor === index
                                    ? "active"
                                    : ""
                                    }`}
                                onClick={() =>
                                    setSelectedColor(index)
                                }
                            >
                                <div className="women-options-preview-image">
                                    <img
                                        src={color.image || product.image}
                                        alt={`${product.title} ${color.name}`}
                                        onError={(e) => {
                                            e.currentTarget.src = "/assets/images/products/women-fas/women-1.png";
                                        }}
                                    />
                                </div>

                                <span
                                    style={{
                                        backgroundColor:
                                            color.value,
                                    }}
                                />

                                <small>{color.name}</small>
                            </button>
                        ))}
                    </div>
                </div>

                <div className="women-options-right">
                    <div className="women-options-brand">
                        <span>SALAR FASHION</span>
                        <div className="women-options-rating">
                            <Star
                                size={13}
                                strokeWidth={2}
                                fill="currentColor"
                            />
                            <strong>
                                {product.rating.toFixed(1)}
                            </strong>
                            <small>({product.reviews})</small>
                        </div>
                    </div>

                    <h2>{product.title}</h2>
                    <p className="women-options-desc">
                        {product.description}
                    </p>

                    <div
                        className={`women-options-deal-tag ${product.dealType}`}
                    >
                        <span />
                        {product.deal}
                    </div>

                    <div className="women-options-pricing-box">
                        <div className="women-options-price-row">
                            <span className="women-options-currency">
                                ₹
                            </span>
                            <span className="women-options-price">
                                {formatPrice(product.price)}
                            </span>
                            <span className="women-options-discount">
                                {discount}% OFF
                            </span>
                        </div>

                        <div className="women-options-mrp-row">
                            <span>MRP</span>
                            <del>
                                ₹{formatPrice(product.mrp)}
                            </del>
                            <span className="women-options-saving">
                                Save ₹
                                {formatPrice(
                                    product.mrp - product.price
                                )}
                            </span>
                        </div>

                        <div className="women-options-smart-card">
                            <div className="women-options-smart-badge">
                                <BadgePercent
                                    size={14}
                                    strokeWidth={2.1}
                                />
                                <span>SALAR SMART PRICE</span>
                            </div>

                            <div className="women-options-smart-details">
                                <div>
                                    <strong>
                                        ₹
                                        {formatPrice(
                                            smartPrice
                                        )}
                                    </strong>
                                    <span>
                                        Effective price with
                                        reward benefits
                                    </span>
                                </div>

                                <small>
                                    Extra ₹
                                    {formatPrice(smartSaving)}{" "}
                                    savings
                                </small>
                            </div>
                        </div>

                        <div className="women-options-offer-copy">
                            <strong>
                                Save more on this order
                            </strong>

                            <p>
                                Use eligible Salar Shopping
                                Voucher, reward balance or
                                promotional offer at checkout.
                            </p>

                            <button type="button" onClick={handleViewDetails}>
                                View offers
                                <ChevronRight
                                    size={14}
                                    strokeWidth={2}
                                />
                            </button>
                        </div>
                    </div>

                    <div className="women-options-selector">
                        <div className="women-options-selector-heading">
                            <div>
                                <strong>Colours</strong>
                                <span>
                                    {
                                        product.colors[
                                            selectedColor
                                        ]?.name
                                    }
                                </span>
                            </div>
                        </div>

                        <div className="women-options-colours">
                            {product.colors.map(
                                (color, index) => (
                                    <button
                                        type="button"
                                        key={color.name}
                                        className={`women-options-colour ${selectedColor ===
                                            index
                                            ? "active"
                                            : ""
                                            }`}
                                        onClick={() =>
                                            setSelectedColor(
                                                index
                                            )
                                        }
                                    >
                                        <span
                                            style={{
                                                backgroundColor:
                                                    color.value,
                                            }}
                                        />

                                        <small>
                                            {color.name}
                                        </small>

                                        {selectedColor ===
                                            index && (
                                                <Check
                                                    size={12}
                                                    strokeWidth={
                                                        2.5
                                                    }
                                                />
                                            )}
                                    </button>
                                )
                            )}
                        </div>
                    </div>

                    <div className="women-options-selector">
                        <div className="women-options-selector-heading">
                            <div>
                                <strong>
                                    Select Size
                                </strong>
                                <span>
                                    {selectedSize}
                                </span>
                            </div>

                            <button type="button">
                                Size guide
                            </button>
                        </div>

                        <div className="women-options-sizes">
                            {availableSizes.map((size) => (
                                <button
                                    type="button"
                                    key={size}
                                    className={
                                        selectedSize ===
                                            size
                                            ? "active"
                                            : ""
                                    }
                                    onClick={() =>
                                        setSelectedSize(
                                            size
                                        )
                                    }
                                >
                                    {size}
                                </button>
                            ))}
                        </div>
                    </div>

                    <div className="women-options-delivery-row">
                        <div>
                            {product.deliveryType ===
                                "delivery" && (
                                    <Truck
                                        size={16}
                                        strokeWidth={1.8}
                                    />
                                )}

                            {product.deliveryType ===
                                "fast" && (
                                    <Zap
                                        size={16}
                                        strokeWidth={1.9}
                                    />
                                )}

                            {product.deliveryType ===
                                "stock" && (
                                    <PackageCheck
                                        size={16}
                                        strokeWidth={1.8}
                                    />
                                )}

                            <span>
                                {product.delivery}
                            </span>
                        </div>

                        <div>
                            <ShieldCheck
                                size={16}
                                strokeWidth={1.8}
                            />
                            <span>
                                Secure purchase
                            </span>
                        </div>
                    </div>

                    <button
                        type="button"
                        className={`women-options-add ${addedToBag ? "added" : ""
                            }`}
                        onClick={handleAddToCart}
                    >
                        {addedToBag ? (
                            <>
                                <Check
                                    size={17}
                                    strokeWidth={2.3}
                                />
                                Added to Cart
                            </>
                        ) : (
                            <>
                                <ShoppingCart
                                    size={17}
                                    strokeWidth={2}
                                />
                                Add to Cart
                            </>
                        )}
                    </button>

                    <button
                        type="button"
                        className="women-options-details"
                        onClick={handleViewDetails}
                    >
                        Product Details
                        <ChevronRight
                            size={15}
                            strokeWidth={2}
                        />
                    </button>
                </div>
            </div>
        </div>
    );
};

export default WomenProductOptionsModal;